import { MatDialog } from '@angular/material';
import { UserinfoService } from '../components/index/service/userinfo.service';
import { environment } from '../../environments/environment';
import { LOGIN_TOKEN_KEY } from '../constants/auth.constants';
import { AuthService } from './auth.service';
import { BehaviorSubject, timer } from 'rxjs';
import { SessionExpiryDialogComponent } from './session-expiry-dialog.component';
import * as i0 from "@angular/core";
import * as i1 from "./auth.service";
import * as i2 from "../components/index/service/userinfo.service";
import * as i3 from "@angular/material/dialog";
var CiamService = /** @class */ (function () {
    function CiamService(authService, userinfoService, dialog) {
        this.authService = authService;
        this.userinfoService = userinfoService;
        this.dialog = dialog;
        this._loginSuccess = true;
        this.loginCompletedSubject = new BehaviorSubject(false);
        this.loginCompleted$ = this.loginCompletedSubject.asObservable();
        this.tokenEnsured = false;
        this.ensuring = null;
        this.isRefresh = false;
        this.expiryCheckSub = null;
        this.promptOpen = false;
    }
    /**
     * Ensures a CIAM token is present (if CIAM enabled) before proceeding.
     * Safe to call multiple times; returns the same Promise while in-flight.
     */
    CiamService.prototype.ensureToken = function () {
        var _this = this;
        if (this.tokenEnsured) {
            return Promise.resolve();
        }
        if (this.ensuring) {
            return this.ensuring;
        }
        this.ensuring = new Promise(function (resolve) {
            // Prefer preauth-provided reload flag; fall back to runtime detection
            var storedReload = sessionStorage.getItem('IS_PAGE_RELOAD');
            if (storedReload !== null) {
                _this.isRefresh = storedReload === 'true';
                try {
                    sessionStorage.removeItem('IS_PAGE_RELOAD');
                }
                catch (_a) { }
            }
            else {
                var navEntries = performance.getEntriesByType('navigation');
                if (navEntries && navEntries.length > 0) {
                    _this.isRefresh = navEntries[0].type === 'reload';
                }
                else if (performance.navigation) { // legacy fallback
                    _this.isRefresh = performance.navigation.type === 1;
                }
            }
            // On hard reload, force a fresh CIAM login and drop stale session data
            if (_this.isRefresh) {
                _this.redirectToCiamLogin();
                _this.loginCompletedSubject.next(true);
                resolve();
                return;
            }
            if (!environment.useCiam) {
                _this.tokenEnsured = true;
                _this.loginCompletedSubject.next(true);
                resolve();
                return;
            }
            // Pre-auth gate may have already performed code exchange
            if (sessionStorage.getItem('CIAM_PREAUTH_DONE')) {
                var existingTokenEarly = sessionStorage.getItem(LOGIN_TOKEN_KEY);
                _this.tokenEnsured = !!existingTokenEarly;
                if (existingTokenEarly) {
                    _this.scheduleExpiryPrompt(existingTokenEarly);
                }
                if (existingTokenEarly && !_this.userinfoService.getUserinfo()) {
                    console.time('[AuthFlow] userinfo_fetch');
                    Promise.resolve(_this.userinfoService.loadUserInfo()).then(function () {
                        console.timeEnd('[AuthFlow] userinfo_fetch');
                        _this.loginCompletedSubject.next(true);
                        resolve();
                    }).catch(function () {
                        _this.loginCompletedSubject.next(true);
                        resolve();
                    });
                }
                else {
                    _this.loginCompletedSubject.next(true);
                    resolve();
                }
                return;
            }
            var onCallback = window.location.href.includes('/auth/cim');
            var existingToken = sessionStorage.getItem(LOGIN_TOKEN_KEY);
            // Case: not callback & no token -> redirect (Angular bootstrap stops after navigation)
            if (!onCallback && !existingToken) {
                _this.redirectToCiamLogin();
                // Do not resolve; navigation away. Mark completed to unblock any waiting logic if Angular continues.
                _this.loginCompletedSubject.next(true);
                resolve();
                return;
            }
            // Case: token already present -> fetch user info if not cached
            if (existingToken) {
                _this.tokenEnsured = true;
                _this.scheduleExpiryPrompt(existingToken);
                if (!_this.userinfoService.getUserinfo()) {
                    console.time('[AuthFlow] userinfo_fetch');
                    Promise.resolve(_this.userinfoService.loadUserInfo()).then(function () {
                        console.timeEnd('[AuthFlow] userinfo_fetch');
                        _this.loginCompletedSubject.next(true);
                        resolve();
                    }).catch(function (err) {
                        console.warn('[CiamService] userinfo fetch failed on existing token', err);
                        _this.loginCompletedSubject.next(true);
                        resolve();
                    });
                }
                else {
                    _this.loginCompletedSubject.next(true);
                    resolve();
                }
                return;
            }
            // Callback route: attempt code exchange
            var url = new URL(window.location.href);
            var code = url.searchParams.get('code');
            console.log('[CiamService] ensureToken code=', code);
            if (!code) {
                console.log('[CiamService] Callback without code -> marking unsuccessful.');
                _this._loginSuccess = false;
                _this.loginCompletedSubject.next(true);
                resolve();
                return;
            }
            console.log('[CiamService] Performing code exchange...');
            console.time('[AuthFlow] code_exchange');
            _this.authService.sendAuthCode(code).toPromise().then(function (res) {
                var token = res && res.body && res.body.token;
                console.log('[CiamService] Code exchange response received. Token present:', !!token);
                console.timeEnd('[AuthFlow] code_exchange');
                if (!token) {
                    _this._loginSuccess = false;
                }
                else {
                    sessionStorage.setItem(LOGIN_TOKEN_KEY, token);
                    _this._loginSuccess = true;
                    _this.scheduleExpiryPrompt(token);
                }
                _this.tokenEnsured = true;
                // Clean URL: drop code parameter
                try {
                    var cleanUrl = window.location.origin + window.location.pathname;
                    window.history.replaceState({}, document.title, cleanUrl);
                }
                catch (_a) { }
                // Prefetch user info immediately (direct injection, no circular now)
                console.time('[AuthFlow] userinfo_fetch');
                Promise.resolve(_this.userinfoService.loadUserInfo()).then(function () {
                    console.timeEnd('[AuthFlow] userinfo_fetch');
                    _this.loginCompletedSubject.next(true);
                    resolve();
                }).catch(function (err) {
                    console.warn('[CiamService] userinfo prefetch failed', err);
                    _this.loginCompletedSubject.next(true);
                    resolve();
                });
            }).catch(function (err) {
                console.log('[CiamService] Code exchange failed:', err);
                _this._loginSuccess = false;
                _this.loginCompletedSubject.next(true);
                resolve();
            });
        });
        return this.ensuring;
    };
    CiamService.prototype.scheduleExpiryPrompt = function (token) {
        var _this = this;
        if (this.expiryCheckSub) {
            this.expiryCheckSub.unsubscribe();
            this.expiryCheckSub = null;
        }
        var expSec = this.getTokenExp(token);
        if (!expSec) {
            return;
        }
        var expTime = Math.max(expSec * 1000 - Date.now(), 0);
        console.log('[CiamService] scheduling token expiry prompt in ms:', expTime);
        var leadMs = 5000; // prompt 5 seconds before expiry
        var due = Math.max(expSec * 1000 - Date.now() - leadMs, 0);
        this.expiryCheckSub = timer(due).subscribe(function () { return _this.showReauthPrompt(); });
    };
    CiamService.prototype.showReauthPrompt = function () {
        var _this = this;
        if (this.promptOpen) {
            return;
        }
        this.promptOpen = true;
        var ref = this.dialog.open(SessionExpiryDialogComponent, { disableClose: true });
        ref.afterClosed().subscribe(function (result) {
            _this.promptOpen = false;
            if (result === 'renew') {
                sessionStorage.clear();
                _this.redirectToCiamLogin();
            }
        });
    };
    CiamService.prototype.getTokenExp = function (token) {
        try {
            var parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }
            var payload = JSON.parse(atob(parts[1]));
            if (payload && typeof payload.exp === 'number') {
                return payload.exp;
            }
        }
        catch (_a) {
            return null;
        }
        return null;
    };
    CiamService.prototype.redirectToCiamLogin = function () {
        if (!environment.ciamUrl) {
            console.warn('CIAM URL not configured');
            return;
        }
        window.location.replace(environment.ciamUrl);
    };
    Object.defineProperty(CiamService.prototype, "loginSuccess", {
        get: function () {
            return this._loginSuccess;
        },
        set: function (success) {
            this._loginSuccess = success;
        },
        enumerable: true,
        configurable: true
    });
    CiamService.ngInjectableDef = i0.defineInjectable({ factory: function CiamService_Factory() { return new CiamService(i0.inject(i1.AuthService), i0.inject(i2.UserinfoService), i0.inject(i3.MatDialog)); }, token: CiamService, providedIn: "root" });
    return CiamService;
}());
export { CiamService };
